import { APIClient } from './api';

declare global {
  interface Window {
    LivekitClient?: any;
    LiveKitClient?: any;
    livekit?: any;
  }
}

interface TranscriptPayload {
  type: 'transcript';
  room: string;
  identity: string;
  text: string;
  language: string;
  voice: string;
  emotion: string;
  provider: string;
  requestId: string;
  characterId?: string;
  characterName?: string;
  characterRole?: string;
  characterPurpose?: string;
}

export class LiveKitTestUI {
  private container: HTMLElement;
  private client: APIClient;
  private room: any | null = null;
  private roomName = '';
  private identity = '';
  private decoder = new TextDecoder();
  private sdk: any | null = null;
  private autoRoomPerConnect = true;
  private attachedAudioElements: HTMLMediaElement[] = [];

  constructor(container: HTMLElement, client: APIClient) {
    this.container = container;
    this.client = client;
  }

  render(): void {
    this.roomName = this.defaultRoomName();
    this.identity = this.defaultIdentity();

    this.container.innerHTML = `
      <div class="test-section">
        <h2>LiveKit Transcript Test</h2>
        <p class="description">Mimic iOS transcript publishing over LiveKit data channel (text input only).</p>

        <div class="controls">
          <div class="control-group">
            <label>Room</label>
            <input id="lk-room" type="text" value="${this.escapeHtml(this.roomName)}" />
          </div>
          <div class="control-group">
            <label style="display:flex; align-items:center; gap:8px; margin-top:22px;">
              <input id="lk-auto-room" type="checkbox" checked />
              New Room Per Connect
            </label>
          </div>
          <div class="control-group">
            <label style="display:flex; align-items:center; gap:8px; margin-top:22px;">
              <input id="lk-mic-enabled" type="checkbox" />
              Enable Microphone
            </label>
          </div>
          <div class="control-group">
            <label>Identity</label>
            <input id="lk-identity" type="text" value="${this.escapeHtml(this.identity)}" />
          </div>
          <div class="control-group">
            <label>Language</label>
            <input id="lk-language" type="text" value="ja-JP" />
          </div>
          <div class="control-group">
            <label>Character ID</label>
            <input id="lk-character-id" type="text" value="wolfchan" />
          </div>
          <div class="control-group">
            <label>Character Name</label>
            <input id="lk-character-name" type="text" value="Wolfchan" />
          </div>
          <div class="control-group">
            <label>Character Role</label>
            <input id="lk-character-role" type="text" value="general AI assistant" />
          </div>
          <div class="control-group">
            <label>Character Purpose</label>
            <input id="lk-character-purpose" type="text" value="help the user in the Consenger world" />
          </div>
          <div class="control-group">
            <label>Voice</label>
            <input id="lk-voice" type="text" value="" />
          </div>
          <div class="control-group">
            <label>Emotion</label>
            <input id="lk-emotion" type="text" value="neutral" />
          </div>
          <div class="control-group">
            <label>Provider</label>
            <input id="lk-provider" type="text" value="auto" />
          </div>
        </div>

        <div class="button-group" style="margin-top: 12px;">
          <button id="lk-connect" class="btn btn-primary">Connect</button>
          <button id="lk-disconnect" class="btn btn-secondary" disabled>Disconnect</button>
          <button id="lk-clear-log" class="btn btn-secondary">Clear Log</button>
        </div>

        <div style="margin-top: 14px;">
          <label for="lk-text"><strong>Transcript Text</strong></label>
          <textarea id="lk-text" rows="4" placeholder="Type transcript text to send..."></textarea>
          <div class="button-group" style="margin-top: 8px;">
            <button id="lk-send" class="btn btn-primary" disabled>Send Transcript</button>
          </div>
        </div>

        <div id="lk-status" class="response-container" style="display:block; margin-top: 14px;">
          <div>State: disconnected</div>
        </div>

        <div id="lk-audio" class="response-container" style="display:block; margin-top: 14px;">
          <div><strong>Audio Output</strong></div>
          <div id="lk-audio-status" style="font-size: 12px; color: #64748b; margin-top: 6px;">
            Waiting for remote audio track...
          </div>
          <div id="lk-audio-container" style="margin-top: 8px;"></div>
        </div>

        <div id="lk-log" class="response-container" style="display:block; margin-top: 14px; max-height: 360px; overflow:auto;"></div>
      </div>
    `;

    this.bindEvents();
  }

  destroy(): void {
    void this.disconnect();
    this.container.innerHTML = '';
  }

  private bindEvents(): void {
    this.el<HTMLButtonElement>('lk-connect').addEventListener('click', () => {
      void this.connect();
    });
    this.el<HTMLButtonElement>('lk-disconnect').addEventListener('click', () => {
      void this.disconnect();
    });
    this.el<HTMLButtonElement>('lk-send').addEventListener('click', () => {
      void this.sendTranscript();
    });
    this.el<HTMLButtonElement>('lk-clear-log').addEventListener('click', () => {
      this.el('lk-log').innerHTML = '';
    });
    this.el<HTMLInputElement>('lk-auto-room').addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      this.autoRoomPerConnect = target.checked;
      if (this.autoRoomPerConnect && !this.room) {
        this.el<HTMLInputElement>('lk-room').value = this.defaultRoomName();
      }
    });
  }

  private async connect(): Promise<void> {
    const sdk = await this.getLiveKitSdk();
    if (!sdk) return;
    if (this.room) {
      this.log('info', 'Existing connection detected, disconnecting first...');
      await this.disconnect();
    }

    if (this.autoRoomPerConnect) {
      this.el<HTMLInputElement>('lk-room').value = this.defaultRoomName();
    }
    const roomName = this.el<HTMLInputElement>('lk-room').value.trim();
    const identity = this.el<HTMLInputElement>('lk-identity').value.trim();
    if (!roomName || !identity) {
      this.log('error', 'Room and Identity are required.');
      return;
    }

    try {
      this.setStatus('connecting');
      const tokenResp = await this.client.getLiveKitToken(roomName, identity, identity);
      const { Room, RoomEvent } = sdk;
      const room = new Room();

      room.on(RoomEvent.ConnectionStateChanged, (state: any) => {
        this.setStatus(String(state || 'unknown'));
      });

      room.on(RoomEvent.ParticipantConnected, (participant: any) => {
        this.log('info', `Participant connected: ${participant.identity}`);
      });

      room.on(RoomEvent.ParticipantDisconnected, (participant: any) => {
        this.log('info', `Participant disconnected: ${participant.identity}`);
      });

      room.on(RoomEvent.TrackSubscribed, (track: any, pub: any, participant: any) => {
        this.log('info', `Track subscribed from: ${participant.identity}`);
        if (this.isAudioTrack(track)) {
          void this.attachAudioTrack(track, pub, participant);
        }
      });

      room.on(RoomEvent.TrackUnsubscribed, (track: any, _pub: any, participant: any) => {
        if (this.isAudioTrack(track)) {
          this.detachAudioTrack(track);
          this.log('info', `Audio track unsubscribed from: ${participant?.identity || 'unknown'}`);
        }
      });

      room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant: any, kind: any, topic: string) => {
        let text = '';
        try {
          text = this.decoder.decode(payload);
          const parsed = JSON.parse(text);
          this.log(
            'data',
            `Data from ${participant?.identity || 'unknown'} topic=${topic || 'n/a'} kind=${String(kind)}: ${JSON.stringify(parsed)}`
          );
        } catch {
          this.log(
            'data',
            `Data from ${participant?.identity || 'unknown'} topic=${topic || 'n/a'} kind=${String(kind)}: ${text}`
          );
        }
      });

      await room.connect(tokenResp.url, tokenResp.token);
      this.room = room;
      this.roomName = roomName;
      this.identity = identity;

      // Publish microphone if enabled
      const micEnabled = this.el<HTMLInputElement>('lk-mic-enabled').checked;
      if (micEnabled) {
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
          this.log('success', 'Microphone published');
        } catch (e) {
          this.log('error', `Failed to publish microphone: ${e instanceof Error ? e.message : String(e)}`);
        }
      }

      // Ensure a bot job is created for this room in explicit-dispatch setups.
      try {
        const dispatch = await this.client.createLiveKitDispatch(roomName, {
          metadata: JSON.stringify({ source: 'admin-ui-livekit-test', identity }),
        });
        this.log(
          'info',
          `Dispatch ${dispatch.reused ? 'reused' : 'created'}: room=${dispatch.room} agent=${dispatch.agentName}`
        );
      } catch (dispatchError) {
        this.log(
          'error',
          `Dispatch failed: ${dispatchError instanceof Error ? dispatchError.message : 'unknown error'}`
        );
        await room.disconnect();
        this.room = null;
        this.setStatus('disconnected');
        return;
      }

      this.el<HTMLButtonElement>('lk-connect').disabled = true;
      this.el<HTMLButtonElement>('lk-disconnect').disabled = false;
      this.el<HTMLButtonElement>('lk-send').disabled = false;
      this.log('success', `Connected: room=${roomName} identity=${identity}`);
    } catch (error) {
      this.setStatus('disconnected');
      this.log('error', `Connect failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }

  private async getLiveKitSdk(): Promise<any | null> {
    if (this.sdk) return this.sdk;

    const globalSdk =
      window.LivekitClient ||
      window.LiveKitClient ||
      window.livekit ||
      null;
    if (globalSdk) {
      this.sdk = globalSdk;
      return globalSdk;
    }

    this.log('info', 'LiveKit SDK global not found; loading fallback ESM bundle...');
    try {
      const runtimeImport = new Function(
        'u',
        'return import(u)'
      ) as (u: string) => Promise<any>;
      const mod = await runtimeImport('https://esm.sh/livekit-client@2.15.13?bundle');
      this.sdk = mod;
      return mod;
    } catch (error) {
      this.log(
        'error',
        `Failed to load LiveKit SDK fallback: ${error instanceof Error ? error.message : 'unknown error'}`
      );
      return null;
    }
  }

  private async disconnect(): Promise<void> {
    const room = this.room;
    this.room = null;

    if (room) {
      try {
        // Prevent stale event callbacks from old room instances.
        if (typeof room.removeAllListeners === 'function') {
          room.removeAllListeners();
        }
      } catch {
        // no-op
      }

      try {
        // Avoid UI getting stuck if SDK disconnect hangs.
        await Promise.race([
          room.disconnect(),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch {
        // no-op
      }
    }

    this.clearAttachedAudio();
    this.el<HTMLButtonElement>('lk-connect').disabled = false;
    this.el<HTMLButtonElement>('lk-disconnect').disabled = true;
    this.el<HTMLButtonElement>('lk-send').disabled = true;
    if (this.autoRoomPerConnect) {
      this.el<HTMLInputElement>('lk-room').value = this.defaultRoomName();
    }
    this.setStatus('disconnected');
    this.log('info', 'Disconnected.');
  }

  private isAudioTrack(track: any): boolean {
    try {
      return track?.kind === 'audio' || track?.kind === 1;
    } catch {
      return false;
    }
  }

  private async attachAudioTrack(track: any, pub: any, participant: any): Promise<void> {
    const container = this.el('lk-audio-container');
    const status = this.el('lk-audio-status');
    let el: HTMLMediaElement | null = null;

    try {
      if (typeof track.attach === 'function') {
        el = track.attach() as HTMLMediaElement;
      }
      if (!el) {
        this.log('error', 'Remote audio track attach() not available.');
        return;
      }

      el.autoplay = true;
      el.controls = true;
      el.style.width = '100%';
      el.style.maxWidth = '420px';
      el.style.display = 'block';
      el.style.marginBottom = '8px';
      el.dataset.trackSid = pub?.trackSid || track?.sid || '';
      el.dataset.participantIdentity = participant?.identity || '';

      container.appendChild(el);
      this.attachedAudioElements.push(el);

      try {
        await el.play();
      } catch (playError) {
        this.log(
          'info',
          `Audio attached but browser blocked autoplay. Press Play in controls. (${playError instanceof Error ? playError.message : 'autoplay blocked'})`
        );
      }

      status.textContent = `Receiving audio from ${participant?.identity || 'remote participant'}`;
    } catch (error) {
      this.log(
        'error',
        `Failed to attach remote audio: ${error instanceof Error ? error.message : 'unknown error'}`
      );
    }
  }

  private detachAudioTrack(track: any): void {
    try {
      if (typeof track.detach === 'function') {
        track.detach();
      }
    } catch {
      // no-op
    }

    const sid = track?.sid || '';
    if (!sid) return;

    const kept: HTMLMediaElement[] = [];
    for (const el of this.attachedAudioElements) {
      if (el.dataset.trackSid === sid) {
        try {
          el.pause();
          el.srcObject = null;
        } catch {
          // no-op
        }
        el.remove();
      } else {
        kept.push(el);
      }
    }
    this.attachedAudioElements = kept;

    if (this.attachedAudioElements.length === 0) {
      this.el('lk-audio-status').textContent = 'Waiting for remote audio track...';
    }
  }

  private clearAttachedAudio(): void {
    for (const el of this.attachedAudioElements) {
      try {
        el.pause();
        el.srcObject = null;
      } catch {
        // no-op
      }
      el.remove();
    }
    this.attachedAudioElements = [];
    this.el('lk-audio-container').innerHTML = '';
    this.el('lk-audio-status').textContent = 'Waiting for remote audio track...';
  }

  private async sendTranscript(): Promise<void> {
    if (!this.room) {
      this.log('error', 'Not connected.');
      return;
    }

    const text = this.el<HTMLTextAreaElement>('lk-text').value.trim();
    if (!text) {
      this.log('error', 'Transcript text is empty.');
      return;
    }

    const payload: TranscriptPayload = {
      type: 'transcript',
      room: this.el<HTMLInputElement>('lk-room').value.trim(),
      identity: this.el<HTMLInputElement>('lk-identity').value.trim(),
      text,
      language: this.el<HTMLInputElement>('lk-language').value.trim() || 'ja-JP',
      voice: this.el<HTMLInputElement>('lk-voice').value.trim(),
      emotion: this.el<HTMLInputElement>('lk-emotion').value.trim() || 'neutral',
      provider: this.el<HTMLInputElement>('lk-provider').value.trim() || 'auto',
      requestId: crypto.randomUUID(),
      characterId: this.el<HTMLInputElement>('lk-character-id').value.trim() || undefined,
      characterName: this.el<HTMLInputElement>('lk-character-name').value.trim() || undefined,
      characterRole: this.el<HTMLInputElement>('lk-character-role').value.trim() || undefined,
      characterPurpose: this.el<HTMLInputElement>('lk-character-purpose').value.trim() || undefined,
    };

    try {
      const data = new TextEncoder().encode(JSON.stringify(payload));
      await this.room.localParticipant.publishData(data, {
        reliable: true,
        topic: 'consenger.transcript',
      });
      this.log('success', `Transcript sent requestId=${payload.requestId} text=${payload.text}`);
    } catch (error) {
      this.log('error', `Send failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    }
  }

  private setStatus(state: string): void {
    this.el('lk-status').innerHTML = `<div>State: ${this.escapeHtml(state)}</div>`;
  }

  private log(level: 'info' | 'success' | 'error' | 'data', message: string): void {
    const now = new Date().toLocaleTimeString();
    const color =
      level === 'error' ? '#ef4444' :
        level === 'success' ? '#10b981' :
          level === 'data' ? '#2563eb' : '#64748b';
    const line = document.createElement('div');
    line.style.cssText = `font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; margin: 4px 0; color: ${color};`;
    line.textContent = `[${now}] ${message}`;
    const logEl = this.el('lk-log');
    logEl.appendChild(line);
    logEl.scrollTop = logEl.scrollHeight;
  }

  private defaultIdentity(): string {
    const token = this.client.getToken();
    if (!token) return `admin-ui-${crypto.randomUUID().slice(0, 8)}`;
    const parts = token.split('.');
    if (parts.length < 2) return `admin-ui-${crypto.randomUUID().slice(0, 8)}`;
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return payload.sub || `admin-ui-${crypto.randomUUID().slice(0, 8)}`;
    } catch {
      return `admin-ui-${crypto.randomUUID().slice(0, 8)}`;
    }
  }

  private defaultRoomName(): string {
    const identity = this.defaultIdentity();
    return `consenger-${identity}-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
  }

  private el<T extends HTMLElement = HTMLElement>(id: string): T {
    const found = document.getElementById(id);
    if (!found) {
      throw new Error(`Element not found: ${id}`);
    }
    return found as T;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
